// src/pages/legal/PrivacyPage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import TextPage, { Contact, operatorName } from './TextPage';

function PrivacyPage() {
    return (
        <TextPage title="Privacy policy" updated="27 September 2026">
            <p>
                This explains what data Tournamental handles, why, where it goes and what you can do about it. It is written
                to be read, not to hide anything.
            </p>

            <h2>Who is responsible</h2>
            <p>
                {operatorName()} runs Tournamental and is responsible for the data described here. You can reach them at{' '}
                <Contact />.
            </p>

            <h2>What we handle</h2>
            <ul>
                <li>
                    <strong>Your account:</strong> the email address, name and account ID that come with your Google or Apple
                    sign-in or your email-and-password account (a password itself is only ever held by Google&rsquo;s Firebase
                    Authentication, never by us).
                </li>
                <li>
                    <strong>What you enter:</strong> tournaments, the names of players, rounds, scores, notes and settings, and
                    who your co-organizers are. Player names are personal data of the players: if you enter other people, you
                    are the one who decides that, so please tell them. Anybody with a read-only link you create can see the
                    tournament, including player names, until you stop sharing.
                </li>
                <li>
                    <strong>Guests:</strong> without an account, tournaments stay on your device only and are not sent to us.
                </li>
                <li>
                    <strong>Technical data:</strong> like any web service, our servers and hosting providers see your IP address
                    and request times in short-lived logs. If the app crashes, it may send an error report: the error message,
                    where in the code it happened, the page address (without anything after a &ldquo;?&rdquo;), the app version
                    and your browser type. It contains no tournament data and no names.
                </li>
                <li>
                    <strong>Usage statistics:</strong> only if you say yes to the question the app asks. Then Google Analytics
                    collects anonymous usage data (pages opened, device and browser type, rough location). You can change your
                    answer at any time with &ldquo;Privacy settings&rdquo; at the bottom of every page.
                </li>
            </ul>

            <h2>Why, and on what basis</h2>
            <p>
                To provide the app to you (running your tournaments, syncing your devices, letting co-organizers work together):
                that is the contract between us. To keep the service safe and working, including error reports and short-lived
                logs: our legitimate interest. For usage statistics: your consent, which you can withdraw at any time.
            </p>

            <h2>Where it is stored, and who else is involved</h2>
            <ul>
                <li>
                    <strong>On your device:</strong> if you use offline mode (which switches on by itself after you sign in) or
                    are a guest, a copy of your tournaments is kept in your browser&rsquo;s storage, together with the app
                    itself, so it works without a connection.
                </li>
                <li>
                    <strong>On our server</strong> (hosted with Render): your tournaments and automatic backups of them, in a
                    private storage area.
                </li>
                <li>
                    <strong>Google Firebase:</strong> sign-in (Authentication, including Google and Apple sign-in), the hosting
                    of the app itself, and, only with your consent, Analytics. Google may process data outside your country,
                    including in the USA, under its own safeguards.
                </li>
                <li>
                    <strong>Brevo</strong> (a mail delivery service): sends the account confirmation and password-reset emails
                    on our behalf, which means it briefly handles your email address and name for that purpose.
                </li>
                <li>
                    <strong>Cloudflare:</strong> our domain&rsquo;s infrastructure, and forwards mail sent to our contact
                    address to us.
                </li>
                <li>We do not sell data, show ads or share data with anybody else.</li>
            </ul>

            <h2>How long we keep it</h2>
            <p>
                Until you delete it. Deleting a tournament removes it (a backup of it is kept for a while, so a slip can be
                undone; backups keep only the newest 30). Deleting your account (&ldquo;Delete my account&rdquo; on the account
                page, described at <Link to="/delete-account">how to delete your account</Link>) removes all your data, your backups and your sign-in immediately. Server logs are kept only
                briefly by the hosting providers.
            </p>

            <h2>Your rights</h2>
            <p>
                You can see, correct and delete your data in the app, and take a copy of all of it with you (&ldquo;Download my
                data&rdquo; on the account page). You may withdraw consent for statistics at any time. If you
                are in Switzerland or the EU you also have the right to ask us for a copy of your data, to have it corrected or
                erased, to object to how we use it, and to complain to your data protection authority. Write to <Contact />{' '}
                and we will answer.
            </p>

            <h2>What is stored in your browser</h2>
            <p>
                The app stores in your browser only what it needs to work: your sign-in session, your tournaments for offline
                use, the downloaded app and engine files, and your choices (such as the answer about statistics). It uses no
                advertising or tracking cookies. Google Analytics may set its own identifiers, but only after you agree.
            </p>

            <h2>Children</h2>
            <p>The app is not meant for children under 16 to sign up on their own.</p>

            <h2>Changes</h2>
            <p>If this changes in a way that matters, the date above changes and the app will tell you.</p>
        </TextPage>
    );
}

export default PrivacyPage;
